import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { getPuckPageQueryKey } from "./useGetPuckPage";
import { PuckPage } from "./useCreatePuckPage";
import { z } from "zod";
import { clearPersistedCache } from "src/_Globals/queryClient/cachePersistence";

type UpdatePuckPageOptions = {
    slug: string;
    data: {
        title?: string;
        content?: string;
        url?: string;
        status?: "active" | "draft";
        allowedRoles?: string[];
        sectors?: string[];
    };
};

export const useUpdatePuckPage = () => {
    const queryClient = useQueryClient();

    return useMutation<PuckPage, Error, UpdatePuckPageOptions>({
        mutationFn: async (options: UpdatePuckPageOptions) => {
            try {
                const validatedOptions = UpdatePuckPageOptionsSchema.parse(options);
                const url = `/api/cms/puck/page/${validatedOptions.slug}`;

                const response = await loggedInApiCall.patch<PuckPage>(url, validatedOptions.data, {
                    headers: {
                        "Content-Type": "application/ld+json",
                    },
                });

                return PuckUpdateResponseSchema.parse(response.data);
            } catch (error) {
                if (error instanceof Error) {
                    throw error;
                }
                throw new Error("Failed to update Puck page: Unknown error", { cause: error });
            }
        },
        onSuccess: (data, variables) => {
            clearPersistedCache();

            queryClient.removeQueries([getPuckPageQueryKey, variables.slug]);
            queryClient.removeQueries([getPuckPageQueryKey, data.slug]);
        },
    });
};

export const UpdatePuckPageOptionsSchema: z.ZodSchema<UpdatePuckPageOptions> = z.object({
    slug: z.string(),
    data: z.object({
        title: z.string().optional(),
        content: z.string().optional(),
        url: z.string().optional(),
        status: z.enum(["active", "draft"]).optional(),
        allowedRoles: z.array(z.string()).optional(),
        sectors: z.array(z.string()).optional(),
    }),
});

export const PuckUpdateResponseSchema: z.ZodSchema<PuckPage> = z.object({
    slug: z.string(),
    title: z.string(),
    content: z.string(),
    locale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    url: z.string().optional(),
    pageType: z.enum(["CMS", "PDF"]).optional(),
    status: z.enum(["active", "draft"]).optional(),
    allowedRoles: z.array(z.string()).nullish(),
    sectors: z.array(z.string()).nullish(),
});
